import { useState, useRef } from "react";
import { PaymentElement, useStripe, useElements } from "@stripe/react-stripe-js";
import { track } from "@crm/analytics";
import { getProductId } from "../utils/billingHelpers";
import { formatPaymentError } from "../utils/paymentErrorHelper";
import { Bluelagoon } from "@bluelagoon";

// Payment method icon component
function PaymentMethodIcon({ type, icon, brand }) {
    // Card brands
    if (type === "card") {
        switch (brand?.toLowerCase()) {
            case "visa":
                return <span className="text-blue-600">💳</span>;
            case "mastercard":
                return <span className="text-orange-500">💳</span>;
            case "amex":
                return <span className="text-blue-400">💳</span>;
            default:
                return <span>💳</span>;
        }
    }

    // Other payment types
    switch (icon || type) {
        // Bank accounts
        case "bank":
        case "us_bank_account":
        case "sepa_debit":
        case "acss_debit":
        case "bacs_debit":
        case "au_becs_debit":
        case "ideal":
            return <span>🏦</span>;

        // Digital wallets
        case "link":
            return <span className="text-green-500">🔗</span>;
        case "cashapp":
            return <span className="text-green-400">💵</span>;
        case "amazon":
        case "amazon_pay":
            return <span className="text-orange-400">📦</span>;
        case "paypal":
            return <span className="text-blue-500">🅿️</span>;
        case "revolut":
        case "revolut_pay":
            return <span className="text-purple-500">💳</span>;
        case "alipay":
            return <span className="text-blue-400">🅰️</span>;
        case "wechat":
        case "wechat_pay":
            return <span className="text-green-500">💬</span>;

        // Buy Now Pay Later
        case "klarna":
            return <span className="text-pink-500">🛒</span>;
        case "affirm":
            return <span className="text-blue-600">✓</span>;
        case "afterpay":
        case "afterpay_clearpay":
            return <span className="text-teal-500">🛍️</span>;

        // Regional methods
        case "pix":
            return <span className="text-teal-400">⚡</span>;
        case "grabpay":
            return <span className="text-green-500">🚗</span>;

        // Crypto
        case "crypto":
            return <span className="text-orange-500">₿</span>;

        default:
            return <span>💰</span>;
    }
}

export function CustomCheckoutForm({
    selectedPlan,
    isYearly,
    onSuccess,
    onCancel,
    userId,
    addPaymentMethod,
    updateSubscription,
    currentUser,
    paymentMethods = [],
    checkoutType = "subscription", // "subscription" or "credit"
    creditAmount,
    onPurchaseCredit,
    t,
    clientSecret,
}) {
    const stripe = useStripe();
    const elements = useElements();
    const [isProcessing, setIsProcessing] = useState(false);
    const submittingRef = useRef(false);
    const [error, setError] = useState(null);
    const [errorDetails, setErrorDetails] = useState(null);
    const [paymentElementComplete, setPaymentElementComplete] = useState(false);
    const [paymentMode, setPaymentMode] = useState(
        paymentMethods.length > 0 ? "existing" : "new"
    );
    const [selectedPaymentMethodId, setSelectedPaymentMethodId] = useState(
        paymentMethods.find((pm) => pm.isDefault)?.id ||
            paymentMethods[0]?.id ||
            null
    );

    // Helper function to get translated plan name
    const getTranslatedPlanName = (plan) => {
        if (!plan) return "";
        if (plan.nameKey) {
            return t(`billing.planNames.${plan.nameKey}`, {
                defaultValue: plan.name,
            });
        }
        return plan.name;
    };

    // Calculate price based on checkout type
    const price =
        checkoutType === "credit"
            ? creditAmount
            : isYearly
            ? selectedPlan.priceYearly
            : selectedPlan.price;
    const totalToday =
        checkoutType === "credit"
            ? creditAmount
            : isYearly
            ? parseFloat(price).toFixed(2)
            : price;
    const savings =
        checkoutType === "subscription" && isYearly
            ? (
                  parseFloat(selectedPlan.price) * 12 -
                  parseFloat(selectedPlan.priceYearly) * 12
              ).toFixed(2)
            : 0;

    const handleSubmit = async (event) => {
        event.preventDefault();

        if (!stripe || !elements) {
            return;
        }

        // Ref-based guard prevents duplicate submissions from rapid clicks
        // (React state updates are async and can miss concurrent clicks)
        if (submittingRef.current) {
            return;
        }
        submittingRef.current = true;

        setIsProcessing(true);
        setError(null);
        setErrorDetails(null);

        try {
            let paymentMethodId = selectedPaymentMethodId;

            // If adding new payment method, use confirmSetup with PaymentElement
            if (paymentMode === "new") {
                // Submit the PaymentElement to validate and collect payment details
                const { error: submitError } = await elements.submit();
                if (submitError) {
                    setError(submitError.message);
                    setIsProcessing(false);
                    return;
                }

                // Confirm the SetupIntent to save the payment method
                const { error: setupError, setupIntent } = await stripe.confirmSetup({
                    elements,
                    confirmParams: {
                        return_url: window.location.href, // Required but won't redirect due to redirect: 'if_required'
                    },
                    redirect: "if_required", // Only redirect if absolutely necessary (e.g., bank redirects)
                });

                if (setupError) {
                    const errorInfo = formatPaymentError(
                        setupError.message || "Failed to set up payment method",
                        { status: 400, code: setupError.decline_code || setupError.code },
                        t
                    );
                    setError(setupError.message);
                    setErrorDetails(errorInfo);
                    setIsProcessing(false);
                    return;
                }

                // Get the payment method ID from the confirmed SetupIntent
                paymentMethodId = setupIntent.payment_method;

                // The payment method is now attached to the customer via the SetupIntent
                // No need to call addPaymentMethod separately - just set it as default
                const setDefaultResponse = await Bluelagoon.setDefaultPaymentMethod({
                    userId,
                    paymentMethodId,
                });

                if (setDefaultResponse?.status !== 200) {
                    console.warn("Failed to set payment method as default:", setDefaultResponse?.error);
                    // Continue anyway - the payment method is still attached
                }
            }

            // Process based on checkout type
            if (checkoutType === "subscription") {
                // Create/update subscription
                // Use the correct productId based on environment (dev vs prod)
                const productId = getProductId(selectedPlan);
                const response = await updateSubscription({
                    userId,
                    productId,
                    isYearly: isYearly,
                    paymentMethodId: paymentMethodId,
                });

                // Check for 3DS requirement first (response.data.data contains the actual data)
                const responseData = response?.data?.data || response?.data;

                if (responseData?.requiresAction && responseData?.clientSecret) {
                    // Handle 3DS inline using Stripe.js
                    // Pass the payment method (required after auth failure)
                    const { error: confirmError, paymentIntent } =
                        await stripe.confirmCardPayment(responseData.clientSecret, {
                            payment_method: responseData.paymentMethodId,
                        });

                    if (confirmError) {
                        const errorInfo = formatPaymentError(
                            confirmError.message || "Authentication failed",
                            { status: 402, code: confirmError.decline_code || confirmError.code },
                            t
                        );
                        setError(confirmError.message || "Authentication failed");
                        setErrorDetails(errorInfo);
                        setIsProcessing(false);
                        return;
                    }

                    if (paymentIntent.status === "succeeded") {
                        // 3DS succeeded - subscription should be active now
                        track("subscription_completed", {
                            plan: selectedPlan.name,
                            isYearly: isYearly,
                            amount: totalToday,
                        });
                        onSuccess();
                    } else {
                        setError(`Payment not completed. Status: ${paymentIntent.status}`);
                    }
                } else if (response?.status === 200 || response?.data?.status === 200) {
                    // Success - subscription created/updated without 3DS
                    track("subscription_completed", {
                        plan: selectedPlan.name,
                        isYearly: isYearly,
                        amount: totalToday,
                    });
                    onSuccess();
                } else {
                    // Other errors
                    const errorInfo = formatPaymentError(
                        response?.data?.error || response?.error || "Failed to process payment",
                        response?.data || response,
                        t
                    );
                    setError(response?.data?.error || response?.error || "Failed to process payment");
                    setErrorDetails(errorInfo);
                }
            } else if (checkoutType === "credit") {
                // Process credit purchase
                const response = await onPurchaseCredit({
                    amount: creditAmount,
                    paymentMethodId: paymentMethodId,
                });

                // Check for 3DS requirement first (response.data.data contains the actual data)
                const responseData = response?.data?.data || response?.data;

                if (responseData?.requiresAction && responseData?.clientSecret) {
                    // Handle 3DS inline using Stripe.js
                    // Pass the payment method (required after auth failure)
                    const { error: confirmError, paymentIntent } =
                        await stripe.confirmCardPayment(responseData.clientSecret, {
                            payment_method: responseData.paymentMethodId,
                        });

                    if (confirmError) {
                        const errorInfo = formatPaymentError(
                            confirmError.message || "Authentication failed",
                            { status: 402, code: confirmError.decline_code || confirmError.code },
                            t
                        );
                        setError(confirmError.message || "Authentication failed");
                        setErrorDetails(errorInfo);
                        setIsProcessing(false);
                        return;
                    }

                    if (paymentIntent.status === "succeeded") {
                        // 3DS succeeded - confirm with backend to add credits
                        const confirmResponse = await Bluelagoon.confirmCreditPayment({
                            paymentIntentId: responseData.paymentIntentId,
                        });

                        if (confirmResponse?.status === 200) {
                            track("credit_purchase_completed", {
                                amount: creditAmount,
                            });
                            onSuccess();
                        } else {
                            setError(confirmResponse?.error || "Failed to add credits");
                            setErrorDetails({
                                title: t?.('billing.errors.generic.title') || 'Payment Error',
                                message: confirmResponse?.error || "Payment succeeded but credits could not be added. Please contact support.",
                                action: "Contact support if credits are not reflected",
                            });
                        }
                    } else {
                        setError(`Payment not completed. Status: ${paymentIntent.status}`);
                    }
                } else if (response?.status === 200 || response?.data?.status === 200) {
                    // Success - payment completed without 3DS
                    track("credit_purchase_completed", {
                        amount: creditAmount,
                    });
                    onSuccess();
                } else {
                    // Other errors
                    const errorInfo = formatPaymentError(
                        response?.data?.error || response?.error || "Failed to purchase credits",
                        response?.data || response,
                        t
                    );
                    setError(response?.data?.error || response?.error || "Failed to purchase credits");
                    setErrorDetails(errorInfo);
                }
            }
        } catch (err) {
            console.error("Error processing payment:", err);

            // Check if this is an API error response (has status, error, code properties)
            const isApiError = err && typeof err === 'object' && 'status' in err;

            // Check if 3DS authentication is required (even in catch block)
            if (isApiError) {
                const requiresAction =
                    err?.code === 'requires_action' ||
                    err?.data?.requiresAction;

                // If requires action with clientSecret, handle inline 3DS
                if (requiresAction && err?.data?.clientSecret && stripe) {
                    try {
                        const { error: confirmError, paymentIntent } =
                            await stripe.confirmCardPayment(err.data.clientSecret);

                        if (!confirmError && paymentIntent?.status === "succeeded") {
                            // For credit purchases, confirm with backend
                            if (checkoutType === "credit" && err.data.paymentIntentId) {
                                const confirmResponse = await Bluelagoon.confirmCreditPayment({
                                    paymentIntentId: err.data.paymentIntentId,
                                });

                                if (confirmResponse?.status === 200) {
                                    track("credit_purchase_completed", { amount: creditAmount });
                                    onSuccess();
                                    return;
                                }
                            } else {
                                // For subscriptions, just call success
                                track("subscription_completed", {
                                    plan: selectedPlan?.name,
                                    isYearly: isYearly,
                                    amount: totalToday,
                                });
                                onSuccess();
                                return;
                            }
                        }

                        if (confirmError) {
                            const errorInfo = formatPaymentError(
                                confirmError.message || "Authentication failed",
                                { status: 402, code: confirmError.decline_code || confirmError.code },
                                t
                            );
                            setError(confirmError.message || "Authentication failed");
                            setErrorDetails(errorInfo);
                            return;
                        }
                    } catch (confirmErr) {
                        console.error("Error during 3DS confirmation:", confirmErr);
                    }
                }
            }

            const errorInfo = formatPaymentError(
                isApiError ? (err.error || "An unexpected error occurred") : (err.message || "An unexpected error occurred"),
                isApiError ? err : { status: 500 },
                t
            );
            setError(isApiError ? (err.error || "An unexpected error occurred") : (err.message || "An unexpected error occurred"));
            setErrorDetails(errorInfo);
        } finally {
            setIsProcessing(false);
            submittingRef.current = false;
        }
    };

    return (
        <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-6">
            {/* Order Summary - Compact */}
            <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl p-4 sm:p-6 border border-blue-100">
                <div className="flex items-center justify-between gap-4">
                    <div className="min-w-0">
                        <h3 className="text-base sm:text-lg font-bold text-gray-900 truncate">
                            {checkoutType === "credit"
                                ? t("billing.purchaseCredits") ||
                                  "Purchase Credits"
                                : getTranslatedPlanName(selectedPlan)}
                        </h3>
                        <p className="text-xs sm:text-sm text-gray-600">
                            {checkoutType === "credit"
                                ? t("billing.oneTimePayment") ||
                                  "One-time payment"
                                : isYearly
                                ? t("billing.billedAnnually") ||
                                  "Billed annually"
                                : t("billing.billedMonthly") ||
                                  "Billed monthly"}
                        </p>
                    </div>
                    <div className="text-right flex-shrink-0">
                        {checkoutType === "subscription" && isYearly ? (
                            <>
                                <span className="text-2xl sm:text-3xl font-bold text-gray-900">
                                    ${(parseFloat(selectedPlan.priceYearly) * 12).toFixed(2)}
                                </span>
                                <span className="text-gray-600 text-sm ml-1">
                                    /yr
                                </span>
                                <div className="text-gray-500 text-xs mt-1">
                                    ${totalToday}/mo × 12 {t("billing.months") || "months"}
                                </div>
                                {savings > 0 && (
                                    <div className="text-emerald-600 text-xs font-medium">
                                        {t("billing.savingsAmount", { amount: savings }) || `Save $${savings}/yr`}
                                    </div>
                                )}
                            </>
                        ) : (
                            <>
                                <span className="text-2xl sm:text-3xl font-bold text-gray-900">
                                    ${totalToday}
                                </span>
                                {checkoutType === "subscription" && (
                                    <span className="text-gray-600 text-sm ml-1">
                                        /mo
                                    </span>
                                )}
                            </>
                        )}
                    </div>
                </div>

                {/* What's Included - Hidden on very small screens */}
                {checkoutType === "subscription" && (
                    <div className="hidden sm:block mt-4 pt-4 border-t border-blue-200">
                        <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-600">
                            <span className="flex items-center gap-1">
                                <svg className="w-3 h-3 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                </svg>
                                {selectedPlan.credits} {t("billing.monthlyCredits") || "credits/mo"}
                            </span>
                            <span className="flex items-center gap-1">
                                <svg className="w-3 h-3 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                </svg>
                                {t("billing.unlimitedApps") || "Unlimited apps"}
                            </span>
                            <span className="flex items-center gap-1">
                                <svg className="w-3 h-3 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                </svg>
                                {t("billing.prioritySupport") || "Support included"}
                            </span>
                        </div>
                    </div>
                )}
            </div>

            {/* Payment Method Selection */}
            <div>
                <label className="block text-sm font-semibold text-gray-700 mb-3">
                    {t("billing.paymentMethod") || "Payment Method"}
                </label>

                {/* Payment Mode Toggle - Only show if user has saved cards */}
                {paymentMethods.length > 0 && (
                    <div className="mb-4 bg-gray-50 rounded-lg p-4 space-y-3">
                        <label className="flex items-center gap-3 cursor-pointer">
                            <input
                                type="radio"
                                name="paymentMode"
                                value="existing"
                                checked={paymentMode === "existing"}
                                onChange={(e) => setPaymentMode(e.target.value)}
                                className="w-4 h-4 text-blue-600"
                            />
                            <span className="text-sm font-medium text-gray-700">
                                {t("billing.useExistingCard") ||
                                    "Use saved card"}
                            </span>
                        </label>

                        <label className="flex items-center gap-3 cursor-pointer">
                            <input
                                type="radio"
                                name="paymentMode"
                                value="new"
                                checked={paymentMode === "new"}
                                onChange={(e) => setPaymentMode(e.target.value)}
                                className="w-4 h-4 text-blue-600"
                            />
                            <span className="text-sm font-medium text-gray-700">
                                {t("billing.addNewCard") || "Add new card"}
                            </span>
                        </label>
                    </div>
                )}

                {/* Existing Payment Method Selection */}
                {paymentMode === "existing" && paymentMethods.length > 0 && (
                    <div className="space-y-2">
                        {paymentMethods.map((pm) => (
                            <label
                                key={pm.id}
                                className={`flex items-center gap-3 p-4 border-2 rounded-lg cursor-pointer transition-all ${
                                    selectedPaymentMethodId === pm.id
                                        ? "border-blue-500 bg-blue-50"
                                        : "border-gray-200 hover:border-gray-300 bg-white"
                                }`}
                            >
                                <input
                                    type="radio"
                                    name="selectedPaymentMethod"
                                    value={pm.id}
                                    checked={selectedPaymentMethodId === pm.id}
                                    onChange={(e) =>
                                        setSelectedPaymentMethodId(
                                            e.target.value
                                        )
                                    }
                                    className="w-4 h-4 text-blue-600"
                                />
                                <div className="flex-1 flex items-center justify-between">
                                    <div className="flex items-center gap-3">
                                        <div className="text-2xl">
                                            <PaymentMethodIcon type={pm.type} icon={pm.icon} brand={pm.brand} />
                                        </div>
                                        <div>
                                            <div className="font-medium text-gray-900 capitalize">
                                                {pm.displayName || `${pm.brand} •••• ${pm.last4}`}
                                            </div>
                                            <div className="text-xs text-gray-500">
                                                {pm.type === "card" && pm.expMonth && pm.expYear && (
                                                    <>Expires {pm.expMonth}/{pm.expYear}</>
                                                )}
                                                {pm.type === "us_bank_account" && pm.accountType && (
                                                    <>{pm.accountType} account</>
                                                )}
                                                {pm.type === "link" && "Stripe Link"}
                                                {pm.type === "cashapp" && "Cash App"}
                                                {pm.type === "amazon_pay" && "Amazon Pay"}
                                                {pm.type === "sepa_debit" && pm.country && (
                                                    <>SEPA ({pm.country})</>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                    {pm.isDefault && (
                                        <span className="text-xs font-semibold text-blue-600 bg-blue-100 px-2 py-1 rounded">
                                            {t("billing.default") || "Default"}
                                        </span>
                                    )}
                                </div>
                            </label>
                        ))}
                    </div>
                )}

                {/* New Payment Method Input - PaymentElement supports cards, crypto, wallets, etc. */}
                {paymentMode === "new" && (
                    <div>
                        <div>
                            <PaymentElement
                                options={{
                                    layout: "tabs",
                                    paymentMethodOrder: ["card", "crypto", "apple_pay", "google_pay", "link"],
                                }}
                                onChange={(e) => setPaymentElementComplete(e.complete)}
                            />
                        </div>
                        <p className="mt-2 text-xs text-gray-500 flex items-center gap-1">
                            <svg
                                className="w-3 h-3"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                                />
                            </svg>
                            {t("billing.secureEncrypted") ||
                                "Your payment information is encrypted and secure"}
                        </p>
                    </div>
                )}
            </div>

            {/* Error Display */}
            {error && errorDetails && (
                <div className="bg-red-50 border-l-4 border-red-500 p-4 rounded-lg">
                    <div className="flex items-start">
                        <div className="flex-shrink-0">
                            <svg
                                className="w-5 h-5 text-red-500 mt-0.5"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                                />
                            </svg>
                        </div>
                        <div className="ml-3 flex-1">
                            <h4 className="text-sm font-bold text-red-800">
                                {errorDetails.title}
                            </h4>
                            <p className="text-sm text-red-700 mt-1.5 leading-relaxed">
                                {errorDetails.message}
                            </p>
                            {errorDetails.action && (
                                <p className="text-xs text-red-600 mt-2 font-medium">
                                    💡 {errorDetails.action}
                                </p>
                            )}
                        </div>
                        <button
                            type="button"
                            onClick={() => {
                                setError(null);
                                setErrorDetails(null);
                            }}
                            className="ml-3 flex-shrink-0 text-red-400 hover:text-red-600"
                        >
                            <svg
                                className="w-4 h-4"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M6 18L18 6M6 6l12 12"
                                />
                            </svg>
                        </button>
                    </div>
                </div>
            )}

            {/* Submit Button */}
            <div className="pt-4">
                <button
                    type="submit"
                    disabled={
                        !stripe ||
                        isProcessing ||
                        (paymentMode === "new" && !paymentElementComplete) ||
                        (paymentMode === "existing" && !selectedPaymentMethodId)
                    }
                    className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white font-bold py-4 px-6 rounded-xl transition-all transform hover:scale-[1.02] disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none shadow-lg hover:shadow-xl"
                >
                    {isProcessing ? (
                        <span className="flex items-center justify-center gap-2">
                            <svg
                                className="animate-spin h-5 w-5 text-white"
                                xmlns="http://www.w3.org/2000/svg"
                                fill="none"
                                viewBox="0 0 24 24"
                            >
                                <circle
                                    className="opacity-25"
                                    cx="12"
                                    cy="12"
                                    r="10"
                                    stroke="currentColor"
                                    strokeWidth="4"
                                ></circle>
                                <path
                                    className="opacity-75"
                                    fill="currentColor"
                                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                                ></path>
                            </svg>
                            {t("billing.processing") || "Processing..."}
                        </span>
                    ) : (
                        <span className="flex items-center justify-center gap-2">
                            <svg
                                className="w-5 h-5"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                                />
                            </svg>
                            {checkoutType === "credit"
                                ? t("billing.purchaseCredits") ||
                                  "Purchase Credits"
                                : t("billing.startSubscription", {
                                      plan: getTranslatedPlanName(selectedPlan),
                                      defaultValue: `Start ${selectedPlan?.name || ""} Plan`,
                                  })}
                        </span>
                    )}
                </button>

                {/* Trust Signals */}
                <div className="mt-4 flex items-center justify-center gap-6 text-xs text-gray-500">
                    <div className="flex items-center gap-1">
                        <svg
                            className="w-4 h-4 text-green-600"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                            />
                        </svg>
                        <span>
                            {t("billing.cancelAnytime") || "Cancel anytime"}
                        </span>
                    </div>
                    <div className="flex items-center gap-1">
                        <svg
                            className="w-4 h-4 text-green-600"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                            />
                        </svg>
                        <span>
                            {t("billing.noHiddenFees") || "No hidden fees"}
                        </span>
                    </div>
                    <div className="flex items-center gap-1">
                        <svg
                            className="w-4 h-4 text-blue-600"
                            fill="currentColor"
                            viewBox="0 0 24 24"
                        >
                            <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z" />
                        </svg>
                        <span>Stripe secured</span>
                    </div>
                </div>
            </div>
        </form>
    );
}
